import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { ShieldCheck, ShieldAlert, UserPlus } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { EmptyState, ErrorState, PageHeader } from '@/components/page';
import { Pagination } from '@/components/pagination';
import { initials } from '@/features/layout/user-menu';
import { StatusPill } from '@/features/register/badges';
import { FilterSelect } from '@/features/register/filter-select';
import { InviteDialog } from '@/features/users/invite-dialog';
import { UserActions } from '@/features/users/user-actions';
import { api } from '@/lib/api';
import { meQuery, useMe } from '@/lib/auth';
import { formatDate, ROLE_LABEL } from '@/lib/labels';
import type { AdminUser, Page, UserStatus } from '@/lib/types';

interface UsersSearch {
  q?: string;
  status?: UserStatus;
  page?: number;
}

export const Route = createFileRoute('/_app/users')({
  validateSearch: (s: Record<string, unknown>): UsersSearch => ({
    q: typeof s.q === 'string' && s.q.trim() ? s.q : undefined,
    status: ['INVITED', 'ACTIVE', 'DISABLED'].includes(s.status as string) ? (s.status as UserStatus) : undefined,
    page: Number(s.page) > 1 ? Math.floor(Number(s.page)) : undefined,
  }),
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQuery);
    if (!me?.canManageUsers) throw redirect({ to: '/entries', search: {} });
  },
  component: UsersPage,
});

const PAGE_SIZE = 50;

function UsersPage() {
  const me = useMe();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [inviting, setInviting] = useState(false);
  const [q, setQ] = useState(search.q ?? '');
  useEffect(() => setQ(search.q ?? ''), [search.q]);
  const superAdmin = me.user.role === 'SUPER_ADMIN';
  const set = (patch: Partial<UsersSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch, page: 'page' in patch ? patch.page : undefined }) });

  const query = useQuery({
    queryKey: ['users', search, me.actingClient?.id ?? null],
    queryFn: () =>
      api.get<Page<AdminUser>>('/v1/users', {
        q: search.q,
        status: search.status,
        clientId: superAdmin ? me.actingClient?.id : undefined,
        page: search.page ?? 1,
        pageSize: PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    set({ q: q.trim() || undefined });
  }

  const scope = superAdmin ? (me.actingClient ? me.actingClient.name : 'all clients') : (me.client?.name ?? '');

  return (
    <>
      <PageHeader
        title="Users"
        description={`People who can sign in to Fraud Safe${scope ? ` for ${scope}` : ''}.`}
        actions={
          <Button onClick={() => setInviting(true)}>
            <UserPlus /> Invite user
          </Button>
        }
      />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <form onSubmit={submit} className="flex gap-2">
          <label htmlFor="users-q" className="sr-only">
            Search users
          </label>
          <Input id="users-q" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name or email" className="h-9 w-64" />
          <Button type="submit" variant="outline" size="sm" className="h-9">
            Search
          </Button>
        </form>
        <FilterSelect
          label="Status"
          value={search.status}
          options={[
            { value: 'ACTIVE', label: 'Active' },
            { value: 'INVITED', label: 'Invited' },
            { value: 'DISABLED', label: 'Disabled' },
          ]}
          onChange={(status) => set({ status })}
        />
      </div>

      {query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <div className="overflow-hidden rounded-xl border bg-card">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted hover:bg-muted">
                <TableHead className="px-4">User</TableHead>
                <TableHead>Role</TableHead>
                {superAdmin ? <TableHead>Client</TableHead> : null}
                <TableHead>Status</TableHead>
                <TableHead>Two-step</TableHead>
                <TableHead>Last sign-in</TableHead>
                <TableHead className="w-12">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {query.isPending
                ? Array.from({ length: 5 }, (_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={superAdmin ? 7 : 6} className="px-4">
                        <Skeleton className="h-9 w-full" />
                      </TableCell>
                    </TableRow>
                  ))
                : query.data.items.map((u) => (
                    <TableRow key={u.id}>
                      <TableCell className="px-4 py-2.5">
                        <div className="flex items-center gap-3">
                          <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-accent text-xs font-semibold text-accent-foreground" aria-hidden="true">
                            {initials(u.name)}
                          </span>
                          <div className="min-w-0">
                            <div className="truncate font-medium">
                              {u.name}
                              {u.id === me.user.id ? <span className="ml-1.5 text-xs font-normal text-muted-foreground">(you)</span> : null}
                            </div>
                            <div className="truncate text-xs text-muted-foreground">{u.email}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{ROLE_LABEL[u.role]}</TableCell>
                      {superAdmin ? <TableCell className="text-muted-foreground">{u.client?.name ?? '—'}</TableCell> : null}
                      <TableCell>
                        {u.locked ? (
                          <StatusPill tone="danger">Locked</StatusPill>
                        ) : u.status === 'ACTIVE' ? (
                          <StatusPill tone="active">Active</StatusPill>
                        ) : u.status === 'INVITED' ? (
                          <StatusPill tone="ended">Invited</StatusPill>
                        ) : (
                          <StatusPill tone="removed">Disabled</StatusPill>
                        )}
                      </TableCell>
                      <TableCell>
                        {u.mfaEnrolled ? (
                          <span className="inline-flex items-center gap-1 text-[13px] text-status-active-fg">
                            <ShieldCheck className="size-4" aria-hidden="true" /> On
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[13px] text-muted-foreground">
                            <ShieldAlert className="size-4" aria-hidden="true" /> Not set up
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-muted-foreground">{u.lastLoginAt ? formatDate(u.lastLoginAt) : 'Never'}</TableCell>
                      <TableCell className="pr-3 text-right">
                        <UserActions me={me} user={u} />
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
          {query.data && query.data.items.length === 0 ? (
            <div className="p-4">
              <EmptyState title="No users found" />
            </div>
          ) : null}
          {query.data ? <Pagination page={query.data.page} pageSize={PAGE_SIZE} total={query.data.total} onPage={(page) => set({ page })} /> : null}
        </div>
      )}
      <InviteDialog me={me} open={inviting} onOpenChange={setInviting} />
    </>
  );
}
